/* Orders — the read-only orders list (/sales/orders). Spec/03 archetype A, reduced: no totals row —
   the backend does not support it yet and it lands in a later step. This is the table (server-side
   sorted and paginated), the filter card (with its chips and status segments), and the record
   modal's own loading/error states.

   Field shapes come from the ACTUAL `excalibur/orders/` list response, which is narrower than the
   detail one: a short `created_by` with no `full_name`/`initials` (`PersonCell` derives both).
   `point`/`supervisor` are nested objects, either of them `null` on an order with no visit — the
   same dash every other empty field gets. The "Сума" column reads `payable_total` — the API's figure
   to pay (after 1C's recalculation when there is one, else the final sum with delivery and
   discount), the same number the order's own "До сплати" shows — never `order_total` (the
   line-item sum only), and sorts on `payable_total`, the same field. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Checkbox, Dropdown, Flex, Form, Input, Segmented, Table, Typography } from "antd";
import { ExportOutlined, LinkOutlined, PushpinOutlined, SearchOutlined, ShopOutlined, TableOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";

import EntitySelect from "../../component/EntitySelect";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import FilterGrid, { FilterCell } from "../../component/FilterGrid";
import PeriodPicker from "../../component/PeriodPicker";
import {
  PersonCell,
  RowActions,
  StatusIcon,
  TableFoot,
  TitleCell,
  actionsColumn,
  dash,
  statusColumn,
  textOf,
  useTableScrollY,
} from "../../component/table-cells";
import { ORDERS_PAGE, PHOTO_REPORT } from "../../config/lang";
import { call, isOk } from "../../util/action";
import dayjs from "../../util/dayjs";
import { formatMoney } from "../../util/formatter";
import { translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

import OrderModal from "./OrderModal";
import { STATUS_ICON } from "./parts";

/* [10, 25, 50, 100]: matches the backend's `StandardResultsSetPagination` default (25), not
   `visit/List.js`'s [10, 20, 50, 100] — that endpoint defaults to 10 for a different reason (see
   the note on `size`'s initial value below). */
const PAGE_SIZES = [10, 25, 50, 100];

/* Segment values, "all" plus the same five keys `STATUS_ICON` already carries — so the segment row
   and the status column say the same thing about a given order, from one enum rather than two. No
   counts on any segment (project-wide rule). */
const STATUS_SEGMENTS = ["all", ...Object.keys(STATUS_ICON)];

/** Which ORDERS_PAGE key names each toggleable column in the Columns panel — same shape as
    `visit/List.js`'s `COLUMN_LABEL`. The status icon, "Замовлення" (the only thing that identifies
    a row) and the row-actions column are absent on purpose: a column missing here never appears in
    the checkbox list at all, which is how the precedent keeps a column mandatory — there is no
    disabled-checkbox state to manage. No persistence, matching the precedent exactly: `hidden`
    resets on every reload, same as `visit/List.js` and `task/List.js`. */
const COLUMN_LABEL = {
  counterparty: "k_counterparty",
  point: "k_point",
  created_by: "k_created_by",
  supervisor: "k_supervisor",
  visit: "k_visit",
  items_count: "col_items_count",
  order_total: "k_amount",
};
const TOGGLEABLE = Object.keys(COLUMN_LABEL);

/* Column key → the field the API orders on, same shape as `visit/List.js`'s `ORDERING` (a column
   missing here is not sortable). `order_total` is the "Сума" column's key, though the column reads
   `payable_total` (see the header note) — mapped here to `payable_total`, the figure it shows. The
   old key is kept on purpose: it is in the `sort` URL param, so renaming it would break saved links
   and their sort — the same `time → duration` kind of mismatch `visit/List.js` already lives with.
   `supervisor` is deliberately absent — a point's responsible supervisors are an M2M
   (`Point.responsible_supervisors`), so ordering across it would duplicate rows. Same reason
   `visit/List.js`'s own supervisor column shows one name but never sorts on it. */
const ORDERING = { name: "name", order_total: "payable_total", point: "visit__point__name" };

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);
const joinIds = list =>
  (list || [])
    .map(idOf)
    .filter(v => v !== undefined && v !== null && v !== "")
    .join(",");
const isoDay = d => dayjs(d).format("YYYY-MM-DD");

/* The default window a fresh visit opens on — the last week, today inclusive. NOT the shared
   `defaultRange()`/`DEFAULT_RANGE_DAYS` from `PeriodPicker.js` (14 days): the product owner chose
   a week over the written spec's 30 days, deliberately, and matches `task/List.js`'s own local
   `defaultTaskRange` shape rather than the shared helper. Applied on the FRONTEND, not requested
   from the backend as an implicit default — otherwise the "Період" field would read empty while
   the table was already narrowed, with nothing on screen saying why. */
const defaultRange = () => [dayjs().subtract(7, "day"), dayjs()];

/** The request the table runs on — one place where the endpoint's filter shape is visible.
    Both date ends are inclusive on the backend, so no `+1 day` correction is needed here (unlike
    `visit/List.js`'s `start__lte`, which matches a datetime rather than a day). */
function buildQuery({ range, counterparty, pointFilter, statuses, createdBy, supervisorFilter, search, sort, page, size }) {
  const p = new URLSearchParams();
  p.set("page", String(page));
  p.set("page_size", String(size));
  if (range?.[0]) p.set("date_from", isoDay(range[0]));
  if (range?.[1]) p.set("date_to", isoDay(range[1]));
  if (counterparty?.length) p.set("counterparty", joinIds(counterparty));
  // Singular, like `counterparty`/`created_by` — the backend param name, not the `EntitySelect`
  // entity name (`points`, plural, which only names where the dropdown's own options come from).
  if (pointFilter?.length) p.set("point", joinIds(pointFilter));
  if (statuses?.length) p.set("status", statuses.join(","));
  if (createdBy?.length) p.set("created_by", joinIds(createdBy));
  if (supervisorFilter?.length) p.set("supervisor", joinIds(supervisorFilter));
  // Matches the order NUMBER (`name`) and the counterparty NAME — nothing else on the row.
  if (search) p.set("search", search);
  // No sort → no `ordering` param at all, so the backend's own default (`-datetime`) applies.
  if (sort) p.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);
  return p.toString();
}

/** URL ↔ state sync, so a filtered view can be shared, bookmarked, and survives coming back from
    an order's page with the browser's Back button.

    Deliberately local — unlike `visit/List.js`/`task/List.js`, nothing outside this file needs
    `buildQuery` or `searchToState`: `orders/Page.js` has no queue-stepping (no "‹ N of M ›" to
    re-run this list's query for), which is the only reason those two lists export them. Matches
    `supervisor/List.js`/`points/List.js`/`merchandiser/MerchList.js`, which export nothing for the
    same reason. `stateToSearch` was never exported anywhere in the app regardless.

    Unlike `visit/List.js`, array filters here are comma-joined in the URL — the SAME encoding
    `buildQuery` sends to the backend, one shape instead of two. `visit` uses repeated params
    (`?users=5&users=8`) because its backend params are ORM-shaped (`start__gte`) and already need
    translating for a shareable URL; ours (`counterparty`, `status`, `created_by`) are already
    clean, so a second encoding would buy nothing. `URLSearchParams` escapes a literal comma to
    `%2C` — legal but unreadable in a link meant to be shared — so it is un-escaped again before
    the string is pushed; a comma is a valid literal in a query value per RFC 3986, and parses back
    unchanged (verified: `new URLSearchParams("a=1,2").get("a") === "1,2"`).

    ⚠ A cleared period writes `period=none`, not nothing. Without it, clearing the period while no
    other filter is active leaves `location.search` completely empty — indistinguishable from a
    genuinely fresh visit, which is exactly what `searchToState` reads as "apply the default week".
    That marker is what makes clearing survive an F5: `search` (the raw string) stays non-empty on
    its own, so the same URL keeps reading as "explicitly no period" rather than "nothing decided
    yet" every time it is reopened. */
function stateToSearch({ range, counterparty, pointFilter, statuses, createdBy, supervisorFilter, search, sort, page, size }) {
  const p = new URLSearchParams();
  if (range) {
    p.set("date_from", isoDay(range[0]));
    p.set("date_to", isoDay(range[1]));
  } else {
    p.set("period", "none");
  }
  if (counterparty?.length) p.set("counterparty", joinIds(counterparty));
  if (pointFilter?.length) p.set("point", joinIds(pointFilter));
  if (statuses?.length) p.set("status", statuses.join(","));
  if (createdBy?.length) p.set("created_by", joinIds(createdBy));
  if (supervisorFilter?.length) p.set("supervisor", joinIds(supervisorFilter));
  if (search) p.set("search", search);
  if (page > 1) p.set("page", String(page));
  if (size !== PAGE_SIZES[1]) p.set("size", String(size));
  if (sort) p.set("sort", `${sort.dir === "descend" ? "-" : ""}${sort.key}`);
  return p.toString().replace(/%2C/g, ",");
}

function searchToState(search) {
  const p = new URLSearchParams(search);
  const dateFrom = p.get("date_from");
  const dateTo = p.get("date_to");
  const sort = p.get("sort");
  const size = Number(p.get("size"));
  return {
    /* The default applies ONLY on a genuinely bare URL — the moment ANY param is present (a
       filter, a sort, even a bare `?page=2`), the link is read literally: a shared URL with no
       period but a counterparty filter means "this counterparty, all time", not "this counterparty,
       last week" silently narrowed. `search` (the raw query string) is what is checked here, not
       any single field — checking, say, `counterparty` alone would still silently default the
       period on a `?page=2`-only link, which is exactly the case this guards.

       This is also what makes an explicit clear survive a reload: `stateToSearch` writes
       `period=none` rather than nothing when the period is cleared, so `search` is never actually
       empty just because clearing happened to be the only active filter — without that marker,
       clearing the period down to "nothing else set either" produced a bare URL that read back as
       a fresh visit on the next load and silently reapplied the default week. `date_from`/`date_to`
       being absent is deliberately NOT read as "cleared" on its own; only the marker means that. */
    range: dateFrom && dateTo ? [dayjs(dateFrom), dayjs(dateTo)] : search ? null : defaultRange(),
    /* Ids only — `EntitySelect` names them once it has fetched the option they belong to (see its
       own doc comment). `counterparty` ids are Excalibur UUIDs, never `Number()`-coerced; `created_by`,
       `point` and `supervisor` ids are the app's own integers. */
    counterparty: (p.get("counterparty") || "")
      .split(",")
      .filter(Boolean)
      .map(id => ({ id })),
    pointFilter: (p.get("point") || "")
      .split(",")
      .filter(Boolean)
      .map(id => ({ id: Number(id) })),
    statuses: (p.get("status") || "").split(",").filter(Boolean),
    createdBy: (p.get("created_by") || "")
      .split(",")
      .filter(Boolean)
      .map(id => ({ id: Number(id) })),
    supervisorFilter: (p.get("supervisor") || "")
      .split(",")
      .filter(Boolean)
      .map(id => ({ id: Number(id) })),
    search: p.get("search") || "",
    page: Math.max(1, Number(p.get("page")) || 1),
    size: PAGE_SIZES.includes(size) ? size : PAGE_SIZES[1],
    sort: sort && ORDERING[sort.replace(/^-/, "")] ? { key: sort.replace(/^-/, ""), dir: sort[0] === "-" ? "descend" : "ascend" } : null,
  };
}

/* A link from a counterparty (its card, an order's counterparty block) hands the name over in route
   state, since the URL carries the id only and `EntitySelect` resolves names from its first page
   of options alone — a counterparty past it showed as its UUID in the chip and the field. A
   self-named `{id, name}` labels both without a request. Only for the exact id it was sent for. */
function withStateNames(state, routeState) {
  const { counterpartyId, counterpartyName } = routeState || {};
  if (!counterpartyId || !counterpartyName) return state;
  return {
    ...state,
    counterparty: state.counterparty.map(v => (String(v.id) === String(counterpartyId) ? { id: v.id, name: counterpartyName } : v)),
  };
}

/** `search` without one parameter — a `supervisor=` a supervisor's own list must not read. */
const withoutParam = (search, key) => {
  const p = new URLSearchParams(search);
  p.delete(key);
  return p.toString();
};

const List = () => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector(state => state.me?.language || "en");
  const t = key => translate(ORDERS_PAGE, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);

  /* A supervisor sees only themselves and their team, so the "Супервайзер" filter is not theirs:
     not drawn, and a `supervisor=` from an old link is dropped before the URL is read — so it never
     reaches the state, the request or a chip, and the URL rewritten from the state loses it. The
     column stays. Every other role keeps the filter. */
  const supervisorShown = useSelector(state => state.me?.role) !== "supervisor";
  /* Read once. After mount this component owns the state and writes the URL, not the reverse —
     reading it back on every change would fight the user's own typing (same as `visit/List.js`). */
  const urlSearch = supervisorShown ? location.search : withoutParam(location.search, "supervisor");
  const initial = useRef(withStateNames(searchToState(urlSearch), location.state)).current;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initial.page);
  /* `PAGE_SIZES[1]` (25), not `[0]`: matches this endpoint's server-side default page size.
     `supervisor/Page.js`'s tabs default the same way, off index 1 rather than 0. */
  const [size, setSize] = useState(initial.size);
  /* Request lifecycle for the table itself — unrelated to the order STATUS filter below, which is
     a value the endpoint filters on, not this page's own loading state.

     `loading` drives `<Table loading>` rather than swapping out the card for a `<Skeleton>`, and
     `error` only changes the empty-state message — the filter card, the table's own header row and
     the footer all stay mounted through every refetch. A `status` tri-state that replaced the whole
     card on every filter change (the shape this file shipped with) is what every OTHER list in the
     app avoids: `visit/List.js`, `supervisor/List.js`, `task/List.js`, `answer/List.js` and
     `merchandiser/MerchList.js` all keep one `loading` boolean and never unmount the table on a
     narrowing filter — only the initial mount and a real failure are visually different from "the
     rows changed". */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  /* ------------------------------------------------------------------- filters
     No period cap — that lands in a later step (spec/03 archetype A in full). Every change
     re-queries the server from page 1, and the whole thing round-trips through the URL below. */
  const [range, setRange] = useState(initial.range);
  const [counterparty, setCounterparty] = useState(initial.counterparty);
  const [pointFilter, setPointFilter] = useState(initial.pointFilter);
  const [statuses, setStatuses] = useState(initial.statuses);
  const [createdBy, setCreatedBy] = useState(initial.createdBy);
  const [supervisorFilter, setSupervisorFilter] = useState(initial.supervisorFilter);
  /* Typing is separate from the applied term, 250ms debounce — the same pattern every search box
     in the app uses (see `supervisor/List.js`). */
  const [typed, setTyped] = useState(initial.search);
  const [search, setSearch] = useState(initial.search);
  const [sort, setSort] = useState(initial.sort);

  /* Which columns are hidden — presentation only, deliberately outside `stateToSearch`/
     `searchToState`: it is not a filter or a sort, so it has no business in a URL meant to be
     shared. Not persisted either, matching `visit/List.js`/`task/List.js` exactly — plain
     `useState`, resets on reload. */
  const [hidden, setHidden] = useState([]);

  const narrow = fn => (...args) => {
    setPage(1);
    fn(...args);
  };

  const resetFilters = narrow(() => {
    setRange(defaultRange());
    setCounterparty([]);
    setPointFilter([]);
    setStatuses([]);
    setCreatedBy([]);
    setSupervisorFilter([]);
    setTyped("");
    setSearch("");
  });

  /* `statuses` stays an array — `status=` on the endpoint takes a comma-joined list — but the
     `Segmented` below is its only control now, so it only ever holds 0 or 1 values in practice.
     The segment shown is a VIEW of `statuses`, not a second state: 0 reads as "all", 1 reads as
     that segment. (A value with no matching option is a supported case for antd's `Segmented`, per
     rc-segmented's own source comment, which is what would make room for a future multi-select
     without the two ever disagreeing.) */
  const segment = statuses.length === 1 ? statuses[0] : "all";
  const setSegment = narrow(value => setStatuses(value === "all" ? [] : [value]));

  /* ---------------------------------------------------------------- chips */

  const { labels, remember, nameFor } = useLabelCache("orders:labels");

  const clearChip = narrow((key, id) => {
    if (key === "period") {
      // Clears, same as the picker's own "x" — a distinct action from "Скинути", which goes to
      // the default week instead.
      setRange(null);
      return;
    }
    if (key === "search") {
      setTyped("");
      setSearch("");
      return;
    }
    if (key === "counterparty") {
      setCounterparty(prev => prev.filter(v => String(idOf(v)) !== String(id)));
      return;
    }
    if (key === "point") {
      setPointFilter(prev => prev.filter(v => String(idOf(v)) !== String(id)));
      return;
    }
    if (key === "createdBy") {
      setCreatedBy(prev => prev.filter(v => String(idOf(v)) !== String(id)));
      return;
    }
    setSupervisorFilter(prev => prev.filter(v => String(idOf(v)) !== String(id)));
  });

  const chips = useMemo(() => {
    const out = [];
    if (range?.[0] && range?.[1]) {
      /* Short form, per spec/04 — `28 Jul → 11 Aug`, year only when the range leaves the current
         one — same formatting `visit/List.js` and `task/List.js` use for their period chip. */
      const thisYear = new Date().getFullYear();
      const sameYear = range[0].year() === thisYear && range[1].year() === thisYear;
      const fmt = new Intl.DateTimeFormat(localeFor(lang), { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
      out.push({ key: "period", label: t("period"), value: `${fmt.format(range[0].toDate())} → ${fmt.format(range[1].toDate())}` });
    }
    counterparty.forEach(v => out.push({ key: "counterparty", id: idOf(v), label: t("k_counterparty"), value: nameFor("counterparty", v) }));
    pointFilter.forEach(v => out.push({ key: "point", id: idOf(v), label: t("k_point"), value: nameFor("point", v) }));
    /* No chip for status: it has no control of its own any more, only the `Segmented` below —
       which already shows the active status, the same as `visit/List.js` and `supervisor/List.js`
       never chip their own `Segmented`. */
    createdBy.forEach(v => out.push({ key: "createdBy", id: idOf(v), label: t("k_created_by"), value: nameFor("createdBy", v) }));
    supervisorFilter.forEach(v => out.push({ key: "supervisor", id: idOf(v), label: t("k_supervisor"), value: nameFor("supervisor", v) }));
    if (search) out.push({ key: "search", label: tp("search"), value: search });
    return out;
    /* `labels` is a dependency: a chip that arrived as an id has to re-render once its name lands
       from `EntitySelect`'s resolution. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, counterparty, pointFilter, statuses, createdBy, supervisorFilter, search, labels, lang]);

  useEffect(() => {
    if (typed === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(typed);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [typed, search]);

  /* The URL trails the state; `replace` — not `push` — so filtering does not fill the history
     stack. Every other list in the app makes the same call, for the same reason (`visit/List.js`'s
     comment): the Back button has to leave the page, not undo one filter change at a time. With a
     250ms-debounced search box, `push` would turn one paused typing session into one history entry
     per pause. */
  useEffect(() => {
    const nextSearch = stateToSearch({ range, counterparty, pointFilter, statuses, createdBy, supervisorFilter, search, sort, page, size });
    if (nextSearch !== location.search.replace(/^\?/, "")) history.replace({ search: nextSearch, state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, counterparty, pointFilter, statuses, createdBy, supervisorFilter, search, sort, page, size]);

  const query = useMemo(() => buildQuery({ range, counterparty, pointFilter, statuses, createdBy, supervisorFilter, search, sort, page, size }), [
    range,
    counterparty,
    pointFilter,
    statuses,
    createdBy,
    supervisorFilter,
    search,
    sort,
    page,
    size,
  ]);

  const load = useCallback(() => {
    setLoading(true);
    call({ method: "GET", url: `excalibur/orders/?${query}` }, response => {
      if (!isOk(response)) {
        setRows([]);
        setTotal(0);
        setError(true);
        setLoading(false);
        return;
      }
      setRows(response.results || []);
      setTotal(response.count || 0);
      setError(false);
      setLoading(false);
    });
  }, [query]);

  useEffect(load, [load]);

  /* ------------------------------------------------------------------ record modal
     `OrderModal` — shared with the counterparty card's Orders tab. Deliberately not
     URL-integrated: the address bar stays on `/sales/orders` while it is open. Routing it is a
     separate change, only worth making if the modal needs to survive a reload. */
  const [modalId, setModalId] = useState(null);
  const closeModal = () => setModalId(null);

  const scrollY = useTableScrollY();
  /* Paging is the footer below, not the table's own pagination, so antd's built-in scroll-to-top on a
     page change never fires — the body is reset here by hand. */
  const tableRef = useRef(null);
  const scrollToTop = () => tableRef.current?.scrollTo({ top: 0 });

  const columns = [
    statusColumn((value, row) => {
      const { Icon, tone } = STATUS_ICON[row.status] || STATUS_ICON.new;
      const label = translate(ORDERS_PAGE, `order_status_${row.status}`, lang) || row.status;
      return <StatusIcon Icon={Icon} tone={tone} label={label} />;
    }),
    {
      key: "name",
      title: t("list_title"),
      width: 220,
      fixed: "left",
      ellipsis: true,
      sorter: true,
      /* Controlled, not left to antd's own internal state (unlike `visit/List.js`, which never
         needs to restore a sort): a page loaded from a shared URL has to show the arrow that
         matches the sort it already applied, not a blank header. Both sortable columns carry this,
         which is what antd's controlled-sorter mode requires — one column's `sortOrder` without
         the other's would leave the two disagreeing about which is active. */
      sortOrder: sort?.key === "name" ? sort.dir : undefined,
      /* The sort arrow is for the order NUMBER only, same as the mockup's single `.srt` header and
         `points/List.js`'s title+sub columns (its "Назва" sorts by `name`, never by the address in
         the sub line). The date underneath has no sort control of its own. */
      render: (value, row) => <TitleCell title={row.name} sub={row.datetime ? dayjs(row.datetime).format("DD.MM.YYYY HH:mm") : null} />,
    },
    {
      key: "counterparty",
      title: t("k_counterparty"),
      width: 220,
      /* Plain text, not a link to the counterparty card (`/sales/counterparties/:id`) — the row
         itself opens the order. Up to three lines, then "…" with the full name in the tooltip (spec/02
         Table contract); `Paragraph`, because `Typography.Text` ignores `rows`. */
      render: (value, row) => {
        const name = textOf(row.counterparty?.name);
        return name === dash ? (
          dash
        ) : (
          <Typography.Paragraph className="lp-wrap" ellipsis={{ rows: 3, tooltip: name }}>
            {name}
          </Typography.Paragraph>
        );
      },
    },
    {
      key: "point",
      title: t("k_point"),
      width: 220,
      ellipsis: true,
      sorter: true,
      sortOrder: sort?.key === "point" ? sort.dir : undefined,
      /* NOT `TitleCell` — checked the mockup's own `pointCell()`: it splits "name — address" and
         renders both halves as `<span>`s on ONE line (name at weight 500, address muted right
         after it), not as `TitleCell`'s two stacked `<div>`s. That stacked shape is what the
         mockup's OWN "Замовлення" cell uses (`<div class="t1">`/`<div class="t2">`) — a different,
         genuinely two-line cell. Composed inline here instead, joined by the mockup's own " — ". */
      render: (value, row) =>
        row.point ? (
          <Typography.Text ellipsis={{ tooltip: [row.point.name, row.point.address].filter(Boolean).join(" — ") }}>
            <Typography.Text strong>{row.point.name}</Typography.Text>
            {row.point.address ? <Typography.Text type="secondary"> — {row.point.address}</Typography.Text> : null}
          </Typography.Text>
        ) : (
          dash
        ),
    },
    {
      key: "created_by",
      title: t("k_created_by"),
      width: 190,
      ellipsis: true,
      render: (value, row) => (row.created_by ? <PersonCell user={row.created_by} strong={false} /> : dash),
    },
    {
      key: "supervisor",
      title: t("k_supervisor"),
      width: 190,
      ellipsis: true,
      render: (value, row) => (row.supervisor ? <PersonCell user={row.supervisor} strong={false} /> : dash),
    },
    {
      key: "visit",
      title: t("k_visit"),
      width: 120,
      render: (value, row) =>
        row.visit ? (
          <Typography.Link
            href={`/visit/${row.visit}`}
            target="_blank"
            rel="noopener noreferrer"
            // The row itself opens the order; this link goes somewhere else.
            onClick={event => event.stopPropagation()}>
            {t("k_visit")} <LinkOutlined />
          </Typography.Link>
        ) : (
          dash
        ),
    },
    {
      key: "items_count",
      title: t("col_items_count"),
      width: 100,
      align: "right",
      render: (value, row) => <span className="lp-num">{textOf(row.items_count)}</span>,
    },
    {
      key: "order_total",
      title: t("k_amount"),
      width: 130,
      align: "right",
      sorter: true,
      sortOrder: sort?.key === "order_total" ? sort.dir : undefined,
      /* `payable_total`, counted by the API — not `order_total`, which is only the line-item sum (see
         the money note in CLAUDE.md). The column's `key` is the old field name; `ORDERING` maps it
         to `payable_total`, the field it shows. */
      render: (value, row) => (
        <Typography.Text strong className="lp-num">
          {formatMoney(row.payable_total)}
        </Typography.Text>
      ),
    },
    actionsColumn((value, row) => (
      <RowActions
        label={t("row_actions")}
        items={[{ key: "open", icon: <ExportOutlined />, label: t("open_page_btn") }]}
        // Carries the filtered view's URL along, so the page's own "До списку" can restore it.
        onClick={() => history.push({ pathname: `/sales/orders/${row.id}`, search: location.search })}
      />
    )),
  ].filter(column => !hidden.includes(column.key));

  return (
    <Flex vertical gap="middle" className="shell-content lp">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {t("list_title")}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </Flex>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        {/* `component={false}`: nothing is submitted — filters apply as they change, and a real
            <form> would turn Enter in the search box into a page reload. */}
        <Form layout="vertical" component={false} colon={false}>
          <FilterGrid>
            <FilterCell wide>
              <Form.Item label={t("period")}>
                {/* No `clearTo` — the picker's own "x" genuinely clears (range: null, all time),
                    distinct from "Скинути", which goes to the default week. `allowClear` is
                    already unconditional in `PeriodPicker.js` itself. */}
                <PeriodPicker value={range} onChange={narrow(setRange)} />
              </Form.Item>
            </FilterCell>
            <FilterCell>
              {/* The endpoint matches this against the order number AND the counterparty name — the
                  placeholder says so ("№ або контрагент") since neither is obvious from the label
                  alone. */}
              <Form.Item label={tp("search")}>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder={t("search_placeholder")}
                  value={typed}
                  onChange={event => setTyped(event.target.value)}
                />
              </Form.Item>
            </FilterCell>

            <FilterCell>
              <Form.Item label={t("k_counterparty")}>
                <EntitySelect
                  entity="counterparty"
                  isMulti
                  value={counterparty}
                  prefix={<ShopOutlined />}
                  onChange={narrow(setCounterparty)}
                  onResolve={remember("counterparty")}
                />
              </Form.Item>
            </FilterCell>
            <FilterCell>
              {/* Same entity `visit/List.js` uses for its own points filter — unscoped here: orders
                  has no county/network filter beside it to narrow the list by. */}
              <Form.Item label={t("k_point")}>
                <EntitySelect
                  entity="points"
                  isMulti
                  value={pointFilter}
                  prefix={<PushpinOutlined />}
                  onChange={narrow(setPointFilter)}
                  onResolve={remember("point")}
                />
              </Form.Item>
            </FilterCell>
            <FilterCell>
              <Form.Item label={t("k_created_by")}>
                {/* Only the roles that create orders in the field — merchandisers and sales
                    representatives, deactivated ones included, since their orders stay. An order
                    created by a supervisor or an order manager has no author to pick here. */}
                <EntitySelect
                  entity="merchandisers_and_sales_representatives"
                  isMulti
                  value={createdBy}
                  prefix={<UserOutlined />}
                  onChange={narrow(setCreatedBy)}
                  onResolve={remember("createdBy")}
                />
              </Form.Item>
            </FilterCell>
            {supervisorShown ? (
              <FilterCell>
                <Form.Item label={t("k_supervisor")}>
                  <EntitySelect
                    entity="supervisor"
                    isMulti
                    value={supervisorFilter}
                    prefix={<TeamOutlined />}
                    onChange={narrow(setSupervisorFilter)}
                    onResolve={remember("supervisor")}
                  />
                </Form.Item>
              </FilterCell>
            ) : null}
          </FilterGrid>

          {/* What is actually applied, in words — the selects collapse to `+N` once a filter
              holds more than one value, so without this row the only way to read the current
              query is to open each dropdown. Same component as every other list. */}
          <FilterChips className="lp-chips" items={chips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <Segmented
              value={segment}
              onChange={setSegment}
              options={STATUS_SEGMENTS.map(value => ({
                value,
                label: value === "all" ? t("seg_all") : translate(ORDERS_PAGE, `order_status_${value}`, lang),
              }))}
            />
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {t("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {t("list_title")}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {t("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
          <span className="lp-spacer" />
          {/* `visit/List.js`'s exact pattern: a plain checkbox list under a `Dropdown`, not a
              `menu` — a menu item swallows the checkbox's own click and closes the panel after
              every single toggle. No reset button — the precedent has none either. */}
          <Dropdown
            trigger={["click"]}
            popupRender={() => (
              <Card className="lp-colcfg" variant="outlined" size="small">
                <Checkbox.Group
                  value={TOGGLEABLE.filter(key => !hidden.includes(key))}
                  onChange={visible => setHidden(TOGGLEABLE.filter(key => !visible.includes(key)))}>
                  <Flex vertical gap={4}>
                    {TOGGLEABLE.map(key => (
                      <Checkbox key={key} value={key}>
                        {t(COLUMN_LABEL[key])}
                      </Checkbox>
                    ))}
                  </Flex>
                </Checkbox.Group>
              </Card>
            )}>
            <Button size="small" icon={<TableOutlined />}>
              {t("columns")}
            </Button>
          </Dropdown>
        </Flex>

        {/* `loading` is antd's own overlay spinner over the existing rows/columns — the filter
            card, this bar and the footer below never unmount, so narrowing a filter reads as
            "the rows changed", not "the page reloaded". A failed request clears the rows and
            says so in the empty state, with a retry, rather than replacing the whole card. */}
        <Table
          ref={tableRef}
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1080, y: scrollY }}
          rowClassName="lp-row"
          onRow={row => ({
            onClick: event => {
              // The row menu owns its click (the visit link stops its own propagation already);
              // everything else opens the record modal.
              if (event.target.closest(".ant-dropdown, .ant-btn")) return;
              setModalId(row.id);
            },
          })}
          /* Paging is the footer below, so the only thing left for the table's own onChange is the
             sorter — with `pagination={false}` its first argument carries nothing. */
          onChange={(pagination, filters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          pagination={false}
          locale={{
            emptyText: error ? (
              <Flex vertical gap={4} align="center">
                <Typography.Text strong>{t("error_title")}</Typography.Text>
                <Button size="small" onClick={load}>
                  {t("retry")}
                </Button>
              </Flex>
            ) : (
              <Flex vertical gap={4} align="center">
                <Typography.Text strong>{t("empty_title")}</Typography.Text>
                <Typography.Text type="secondary">{t("empty_desc")}</Typography.Text>
              </Flex>
            ),
          }}
        />
        <TableFoot
          id="ord"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: t("rows_per_page"), range: t("range_of") }}
          onPage={next => {
            setPage(next);
            scrollToTop();
          }}
          onSize={next => {
            setSize(next);
            setPage(1);
            scrollToTop();
          }}
        />
      </Card>

      {/* "Open page" carries the filtered view's URL along, so the page's own "До списку" can restore it. */}
      <OrderModal id={modalId} lang={lang} onClose={closeModal} openTo={{ pathname: `/sales/orders/${modalId}`, search: location.search }} />
    </Flex>
  );
};

export default List;
